'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { ContextNavigation, ListPageSearch } from '@terra/ui-design-system';
import type { TraceStatusFilter } from '../../../store/tab/tracesTabScopedFilterStore';
import { ListPageRefreshButton } from '../../common/ListPageRefreshButton';
import {
  EXECUTIONS_STATUS_CHIP_ALL,
  EXECUTIONS_STATUS_CHIPS,
  resolveExecutionsStatusChip,
  resolveExecutionsStatusChipCount,
  toExecutionsStatusFilter,
  type ExecutionsStatusChip,
  type ExecutionsStatusCounts,
} from '../executions-table/executions-list.model';

type ExecutionsContextNavigationProps = {
  statusFilter: TraceStatusFilter[];
  onStatusFilterChange: (statusFilter: TraceStatusFilter[]) => void;
  statusCounts: ExecutionsStatusCounts | null;
  searchTerm: string;
  onSearchTermChange: (searchTerm: string) => void;
  onRefresh: () => void;
  isLoading: boolean;
  optionsMenu: ReactNode;
};

export function ExecutionsContextNavigation({
  statusFilter,
  onStatusFilterChange,
  statusCounts,
  searchTerm,
  onSearchTermChange,
  onRefresh,
  isLoading,
  optionsMenu,
}: ExecutionsContextNavigationProps) {
  const tCommon = useTranslations('designSystem.common');
  const tExecutions = useTranslations('designSystem.executions');
  const items = useMemo(
    () =>
      EXECUTIONS_STATUS_CHIPS.map((chip) => ({
        value: chip,
        label:
          chip === EXECUTIONS_STATUS_CHIP_ALL
            ? tCommon('all')
            : tExecutions(`status.${chip}`),
        count: resolveExecutionsStatusChipCount({ chip, statusCounts }),
      })),
    [statusCounts, tCommon, tExecutions],
  );

  return (
    <ContextNavigation<ExecutionsStatusChip>
      ariaLabel={tExecutions('filters.status')}
      items={items}
      value={resolveExecutionsStatusChip(statusFilter)}
      onValueChange={(chip) =>
        onStatusFilterChange(toExecutionsStatusFilter(chip))
      }
      actions={
        <>
          <ListPageSearch
            value={searchTerm}
            onValueChange={onSearchTermChange}
            placeholder={tCommon('searchByNameOrId', {
              entityName: tExecutions('execution'),
            })}
          />
          <ListPageRefreshButton onRefresh={onRefresh} isLoading={isLoading} />
          {optionsMenu}
        </>
      }
    />
  );
}
